import { useEffect, useState, useContext } from "react";
import {
  User as UserIcon,
  Banknote,
  CreditCard,
  ArrowUpRight,
  Save,
} from "lucide-react";
import { fetchCurrentUser, updateUserSettings } from "../api/settings";
import { AuthContext } from "../context/AuthContext";
import CheckoutDrawer from "../components/CheckoutDrawer";
import { useSubmissionLock } from "../hooks/useSubmissionLock";

const emptyProfile = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company: "",
  address: "",
  bankName: "",
  bankAccount: "",
  bankSort: "",
  plan: "Starter",
};

const plans = [
  {
    name: "Starter",
    price: "Freemium forever",
    amount: "0",
    currencySymbol: "£",
    interval: "forever",
    features: ["1 property", "Limited contacts", "Core property management only"],
    highlight: false,
  },
  {
    name: "Beem Plus",
    price: "£29 /month",
    amount: "29",
    currencySymbol: "£",
    interval: "monthly",
    features: [
      "Up to 5 properties",
      "Increased contact allowance",
      "Analytics",
      "Payments",
    ],
    highlight: true,
  },
  {
    name: "Beem Enhanced",
    price: "£69 /month",
    amount: "69",
    currencySymbol: "£",
    interval: "monthly",
    features: [
      "Everything in Plus",
      "Up to 20 properties",
      "Tenant portal",
      "Maintenance Jobs",
      "Contractor SMS links",
      "Quotes, scheduling and proof of work",
    ],
    highlight: false,
  },
  {
    name: "Enterprise",
    price: "Contact us",
    amount: "0",
    currencySymbol: "£",
    interval: "custom",
    features: ["Contact us for enterprise packages."],
    highlight: false,
  },
];

function normalizePlanLabel(value) {
  const raw = String(value || "").toLowerCase();
  if (raw.includes("enhanced")) return "Beem Enhanced";
  if (raw.includes("plus")) return "Beem Plus";
  if (raw.includes("enterprise")) return "Enterprise";
  return "Starter";
}

function getVisiblePlan(user, fallback = "Starter") {
  const role = (user?.role || "").toUpperCase();
  const status = (user?.subscriptionStatus ?? "").toLowerCase();
  const rawPlan = normalizePlanLabel(user?.rawPlan || "");
  const responsePlan = normalizePlanLabel(user?.plan || user?.planName || fallback);
  const plan = rawPlan !== "Starter" ? rawPlan : responsePlan;
  const active = status === "active" || status === "trialing";

  if (role === "ADMIN") return "Beem Enhanced";
  if (!active) return "Starter";
  if (plan === "Beem Enhanced" || plan === "Beem Plus") return plan;
  return "Beem Plus";
}

export default function Settings() {
  const { setUser, refreshMe, user } = useContext(AuthContext);

  // Seed profile from AuthContext.user so fields are not empty while we wait for fetchCurrentUser
  const [profile, setProfile] = useState(() => {
    if (!user) return emptyProfile;
    return {
      ...emptyProfile,
      first_name: user.first_name ?? "",
      last_name: user.last_name ?? "",
      email: user.email ?? "",
      phone: user.phone ?? "",
      company: user.company ?? "",
      address: user.address ?? "",
      bankName: user.bankName ?? "",
      bankAccount: user.bankAccount ?? "",
      bankSort: user.bankSort ?? "",
      plan: user.plan ?? emptyProfile.plan,
    };
  });

  const initialSelectedPlan = getVisiblePlan(user, emptyProfile.plan);


  const [saving, setSaving] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(initialSelectedPlan);
  const [loading, setLoading] = useState(false);
  const { run: runSubmission, isPending } = useSubmissionLock();

  // Load profile from backend
  const loadProfile = async () => {
    setLoading(true);
    try {
      const body = await fetchCurrentUser();
      const userResp = body?.data?.user ?? null;
      const profileJson = body?.data?.profile ?? null;

      const merged = {
        ...emptyProfile,
        ...(userResp
          ? {
              first_name: userResp.first_name ?? "",
              last_name: userResp.last_name ?? "",
              email: userResp.email ?? "",
              phone: userResp.phone ?? "",
              company: userResp.company ?? "",
              address: userResp.address ?? "",
              bankName: userResp.bankName ?? "",
              bankAccount: userResp.bankAccount ?? "",
              bankSort: userResp.bankSort ?? "",
              plan:
                userResp.plan ??
                profileJson?.plan ??
                profile.plan ??
                emptyProfile.plan,
            }
          : {}),
        ...(profileJson ?? {}),
      };

      setProfile(merged);

      setSelectedPlan(
        getVisiblePlan(userResp || user, merged.plan || "Starter")
      );

      // Keep AuthContext in sync with any updated fields
      if (userResp) {
        setUser((prev) => ({ ...(prev || {}), ...userResp }));
      }
    } catch (err) {
      console.error("[Settings] failed to fetch /api/users/me", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setProfile((p) => ({ ...p, [name]: value }));
  }

  function handlePlanChange(planName) {
    setSelectedPlan(planName);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (saving || isPending("settings:save")) return;

    await runSubmission("settings:save", async () => {
      setSaving(true);
      try {
        const allowed = [
          "first_name",
          "last_name",
          "email",
          "phone",
          "company",
          "address",
          "bankName",
          "bankAccount",
          "bankSort",
        ];
        const payload = {};
        for (const k of allowed) payload[k] = profile[k] ?? "";

        const res = await updateUserSettings(payload);
        const updatedUser = res?.data?.user ?? null;
        if (updatedUser) {
          setUser((prev) => ({ ...(prev || {}), ...updatedUser }));
        }

        await loadProfile();
        await refreshMe();

        alert("Settings saved");
      } catch (err) {
        console.error("[Settings] save failed", err);
        alert("Failed to save settings (check server logs).");
      } finally {
        setSaving(false);
      }
    });
  }

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState("PLUS");


function handleUpgrade(planKey = "PLUS") {
  setCheckoutPlan(planKey);
  setCheckoutOpen(true);
}

  return (
    <div className="pt-10 md:pt-0 text-white">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl">Your account</h1>
      </div>
      <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6" />

      {/* Profile Form */}
      <form
        onSubmit={handleSave}
        className="bg-white/10 border border-white/10 rounded-xl p-6 mb-10 shadow-lg glass text-white lg:col-span-1 mb-6"
      >
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <UserIcon className="w-6 h-6 text-[#AF9B8A]" /> Profile Information
        </h2>

        <p className="text-white mb-4 flex items-center gap-2">
          This information will be shown on your tenant's portal.
        </p>

        {loading ? (
          <div className="text-white/70 py-6">Loading profile...</div>
        ) : (
          <>
            {/* Profile Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-xs text-gray-300 mb-1">
                  First Name
                </label>
                <input
                  name="first_name"
                  value={profile.first_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] placeholder-gray-400"
                  required
                  placeholder="First Name"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-300 mb-1">
                  Last Name
                </label>
                <input
                  name="last_name"
                  value={profile.last_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] placeholder-gray-400"
                  required
                  placeholder="Last Name"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-300 mb-1">Email</label>
                <input
                  name="email"
                  type="email"
                  value={profile.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] placeholder-gray-400"
                  required
                  placeholder="Email"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-300 mb-1">Phone</label>
                <input
                  name="phone"
                  value={profile.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] placeholder-gray-400"
                  placeholder="Phone"
                />
              </div>
            </div>

            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2 mt-8">
              <Banknote className="w-6 h-6 text-[#AF9B8A]" /> Bank Details
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {[
                { label: "Bank Name", name: "bankName" },
                { label: "Account Number", name: "bankAccount" },
                { label: "Sort Code", name: "bankSort" },
              ].map((f) => (
                <div key={f.name}>
                  <label className="block text-xs text-gray-300 mb-1">
                    {f.label}
                  </label>
                  <input
                    name={f.name}
                    value={profile[f.name] ?? ""}
                    onChange={handleChange}
                    placeholder={f.label}
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] placeholder-gray-400"
                  />
                </div>
              ))}
            </div>

            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-[#AF9B8A] text-white font-semibold hover:bg-[#AF9B8A]/80 transition mt-2"
              aria-busy={saving}
            >
              <Save className="w-5 h-5" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </>
        )}
      </form>

      {/* Plan Section */}
      <div className="bg-white/10 border border-white/10 rounded-xl p-6 shadow-lg backdrop-blur-md text-white">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-[#AF9B8A]" /> Your Plan
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`rounded-xl p-6 border transition flex flex-col items-start ${
                selectedPlan === plan.name
                  ? "border-[#AF9B8A] bg-[#AF9B8A]/10 shadow-lg"
                  : "border-white/10 bg-white/10"
              } ${plan.highlight ? "ring-2 ring-[#AF9B8A]" : ""}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg font-bold text-white">{plan.name}</span>
                {plan.highlight && (
                  <span className="text-xs bg-[#AF9B8A] text-white px-2 py-0.5 rounded-full">
                    Popular
                  </span>
                )}
              </div>
              <div className="text-2xl font-bold text-[#AF9B8A] mb-2">
                {plan.price}
              </div>
              <ul className="mb-4 space-y-1 text-white/80 text-sm">
                {plan.features.map((f) => (
                  <li key={f}>• {f}</li>
                ))}
              </ul>

              <button
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition
                  ${
                    selectedPlan === plan.name
                      ? "bg-[#AF9B8A] text-white cursor-default"
                      : "bg-white/20 text-white hover:bg-[#AF9B8A]/20 cursor-pointer"
                  }`}
                disabled={selectedPlan === plan.name}
                onClick={() =>
                  plan.name === "Beem Plus"
                    ? handleUpgrade("PLUS")
                    : plan.name === "Beem Enhanced"
                    ? handleUpgrade("ENHANCED")
                    : handlePlanChange(plan.name)
                }
              >
                {selectedPlan === plan.name ? (
                  <span>Current Plan</span>
                ) : (
                  <>
                    <ArrowUpRight className="w-4 h-4" />
                    <span>Upgrade</span>
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>
                                          <CheckoutDrawer
  open={checkoutOpen}
  onClose={() => setCheckoutOpen(false)}
  plan={checkoutPlan}
/>
    </div>
  );
}
